import type Eshop from '@eshop/type/eshop'
import type { OptionalPropsUnion } from '@eshop/type/global'
import type Rental from '@eshop/type/rental'

import { pluralTranslate } from '#/global/helper/i18n'

import type { AddToCartEshopEventInvoker, AddToCartEshopParams } from '#/global/component/Product/AddToCart/Eshop'
import type { AddToCartRentalEventInvoker, AddToCartRentalParams } from '#/global/component/Product/AddToCart/Rental'

import { ServiceProduct } from '#/global/service/Product'

import { Config } from '#/global/helper/Config'

export const ProductHelper = {
    formatPrice(this: void, price: number, currencyCode: string): string {
        const formatedPrice = new Intl.NumberFormat('cs-CZ', { style: 'currency', currency: currencyCode }).format(price)
        return currencyCode === 'EUR' ? formatedPrice.replace('€', 'EUR') : formatedPrice.toString()
    },

    formatUnit(this: void, unit: string) {
        return unit.replace('m2', 'm²').replace('m3', 'm³')
    },

    formatQuantity(this: void, quantity: number) {
        return (Number(quantity) === quantity && quantity % 1 !== 0 ? quantity.toFixed(2) : quantity).toLocaleString().replace('.', ',')
    },

    calculateQuantityUnitEshopToSales(this: void, quantityPrimary: number, unit: Eshop.Unit): Eshop.QuantityUnit {
        const quantitySales = unit.isPackagePrimary
            ? (quantityPrimary * 1e+6) * (unit.unitsInPackage * 1e+6) / 1e+12
            : quantityPrimary

        return {
            unitPrimary: quantityPrimary,
            unitSecondary: unit.isPackagePrimary ? quantitySales : quantityPrimary,
            unitSales: quantitySales,
            unitPackage: unit.isPackagePrimary ? quantityPrimary : quantitySales,
        }

        // Nevi se, co to melo fixovat, ale unitSales je bugy
        // const quantitySecondery = (quantityPrimary * 1e+6) * (unit.unitsInPackage * 1e+6) / 1e+12

        // return {
        //     unitPrimary: quantityPrimary,
        //     unitSecondary: quantitySecondery,
        //     unitSales: quantitySecondery, // bugy line
        //     unitPackage: unit.isPackagePrimary ? quantityPrimary : quantitySecondery,
        // }
    },

    /**
     * @deprecated Use {@link calculateQuantityUnitEshopToSales} instead
     */
    calculateQuantityUnitEshop(this: void, quantityPrimary: number, unit: Eshop.Unit): Eshop.QuantityUnit {
        return ProductHelper.calculateQuantityUnitEshopToSales(quantityPrimary, unit)
    },

    calculateQuantityUnitEshopToPrimary(this: void, quantitySales: number, unit: Eshop.Unit): Eshop.QuantityUnit {
        const quantityPrimary = unit.isPackagePrimary
            ? (quantitySales * 1e+12) / (unit.unitsInPackage * 1e+6) / 1e+6
            : quantitySales

        return {
            unitPrimary: quantityPrimary,
            unitSecondary: unit.isPackagePrimary ? quantitySales : quantityPrimary,
            unitSales: quantitySales,
            unitPackage: unit.isPackagePrimary ? quantityPrimary : quantitySales,
        }

        // Nevi se, co to melo fixovat, ale unitSales je bugy
        // const quantitySecondery = (quantityPrimary * 1e+6) * (unit.unitsInPackage * 1e+6) / 1e+12

        // return {
        //     unitPrimary: quantityPrimary,
        //     unitSecondary: quantitySecondery,
        //     unitSales: quantitySecondery, // bugy line
        //     unitPackage: unit.isPackagePrimary ? quantityPrimary : quantitySecondery,
        // }
    },

    calculateQuantityPriceEshop(this: void, quantityPrimary: number, price: Eshop.Price): Eshop.QuantityPrice {
        // Do not change. It is based on the formula from [funct].czES_GetCenasDPH *}
        const priceNoVat = Math.round(price.priceNoVatPrimary * quantityPrimary * 100) / 100
        const vat = Math.round((priceNoVat * price.vat / 100) * 100) / 100
        const priceVat = Math.round((priceNoVat + vat) * 100) / 100

        return {
            priceNoVatPrimary: priceNoVat,
            priceVatPrimary: priceVat,
        }
    },

    /**
     * Detailnější zobrazení [zde na wiki](https://wiki.web.dek.cz/books/eshop/page/stavove-kody-dostupnosti-polozek)
     * **code** | **status**
     * :-- | :--:
     * --- | ---
     * **P00** | někde skladem -> není pobočka
     * **P01** | skladem na pobočce
     * **P05** | skladem u dodavatele
     * **P06** | skladem na centrále
     * --- | ---
     * **P02** | aktuálně vyprodáno -> není nikde skladem
     * **P07** | aktuálně vyprodáno -> na konkrétní pobočce a nebo globálně
     * **P08** | aktuálně vyprodáno
     * **P10** | aktuálně vyprodáno -> není skladem na konkrétní pobočce
     * --- | ---
     * **P04** | není skladem -> na konkrétní pobočce
     * --- | ---
     * **P03** | pouze poptávka
     * --- | ---
     * **P09** | nedostupné na eshopu -> položka už se neprodává
     */
    getAvailabilityStatusByBranchCode(this: void, availability: Eshop.Availability, demand: string, toCart: string): ({ buttonColor: 'green' | 'red' | 'blue' | 'gray', buttonText: string, showButton: true } | { showButton: false }) & { color: 'green' | 'red' | 'blue' | 'gray' } {
        switch (availability.availabilityBranchCode) {
            case 'P00':
            case 'P01':
            case 'P05':
            case 'P06':
                return {
                    showButton: true,
                    color: 'green',
                    buttonText: toCart,
                    buttonColor: 'green',
                }
            case 'P02':
            case 'P07':
            case 'P08':
            case 'P10':
                return {
                    showButton: false,
                    color: 'red',
                }
            case 'P03':
                return {
                    showButton: true,
                    color: 'blue',
                    buttonText: demand,
                    buttonColor: 'blue',
                }
            case 'P04':
                return {
                    showButton: true,
                    color: 'red',
                    buttonText: demand,
                    buttonColor: 'blue',
                }
            case 'P09':
                return {
                    showButton: false,
                    color: 'gray',
                }
            default:
                return {
                    showButton: true,
                    color: 'blue',
                    buttonText: demand,
                    buttonColor: 'blue',
                }
        }
    },

    addToCartEshop(this: void, code: string, quantitySales: number, unitSales: string, eventInvoker: AddToCartEshopEventInvoker) {
        window.dispatchEvent(new CustomEvent<AddToCartEshopParams>('ES_ADD_TO_CART_ESHOP', { detail: {
            code,
            quantitySales,
            unitSales,
            eventInvoker,
        } }))
    },

    addToCartRental(this: void, props: {
        date: { from: Date, to: Date }
        eventInvoker: AddToCartRentalEventInvoker
        hasGuarantee?: boolean
        quantity: AddToCartRentalParams['quantitySales']
        typeOfRental: AddToCartRentalParams['typeOfRental']
        unionCode: AddToCartRentalParams['unionCode']
    }) {
        const { date, eventInvoker, hasGuarantee, quantity, typeOfRental, unionCode } = props
        window.dispatchEvent(new CustomEvent<AddToCartRentalParams>('ES_ADD_TO_CART_RENTAL', { detail: {
            unionCode,
            quantity,
            quantitySales: quantity,
            dateFrom: date.from,
            dateTo: date.to,
            withGuarantee: hasGuarantee,
            typeOfRental,
            eventInvoker,
        } }))
    },

    /**
     * @deprecated - Use ServiceProduct.getProductEshopByCode() instead
     */
    async getProductEshopByCode<T extends OptionalPropsUnion<Eshop.Body>>(this: void, codes: string | string[], parts: Array<T>) {
        return ServiceProduct.getProductEshopByCode(codes, parts)
    },

    /**
     * @deprecated - Use ServiceProduct.getProductRentalByCode() instead
     */
    async getProductRentalByCode<T extends OptionalPropsUnion<Rental.Body>>(this: void, codes: string | string[], parts: Array<T>) {
        return ServiceProduct.getProductRentalByCode(codes, parts)
    },

    /**
     * Return the number of points that the customer will receive for the purchase of the product
     */
    calculateSeasonPoints(this: void, product: Eshop.Product<'season' | 'price'>, quantitySales: number) {
        if (product.season === null || product.season === undefined) {
            return 0
        }
        const season = product.season
        if (season.rewardFor === 'Quantity') {
            return Math.floor(quantitySales / season.requiredQuantity) * season.rewardPoints
        }
        if (season.rewardFor === 'Price') {
            const totalPriceNoVatSales = product.price.priceNoVatSales * quantitySales
            return Math.floor(totalPriceNoVatSales / season.requiredQuantity) * season.rewardPoints
        }
        return 0
    },

    getNumberOfDaysByTypeOfRental(this: void, date: { from: Date, to: Date }, typeOfRental: AddToCartRentalParams['typeOfRental']) {
        if (typeOfRental === 'HalfDay') {
            return '4 hodiny'
        }
        // 86400000 -> 24 * 60 * 60 * 1000 -> 24 hours -> 60 minutes in 1 hour -> 60 seconds in 1 minute -> 1000 milliseconds in one second
        const days = Math.ceil((date.to.getTime() - date.from.getTime()) / 86_400_000)

        if (typeOfRental === 'FullDay') {
            return translate(days)
        }
        // + 1 because of the first day
        return translate(days + 1)
    },
}
// var totalPriceNoVat = Math.round(priceNoVatUnitSales * totalQuantity * 100) / 100;

const translate = (days: number) => pluralTranslate(Config.getLang() === 'sk' ? 'deň | dni | dní' : 'den | dny | dní', days)
